// Web Push client — registers the service worker and manages this device's push
// subscription. The backend (server/app/webpush.py) sends pushes; sw.js renders them.
//
// Flow: registerServiceWorker() runs at boot (main.tsx). enablePush() is user-initiated
// from Settings → Notifications: it asks permission, subscribes via the VAPID key, and
// POSTs the subscription to the API. disablePush() reverses it.
import { api } from './api';

export type PushState =
  | 'unsupported' // no service worker / Push API (e.g. iOS Safari not installed to Home Screen)
  | 'unconfigured' // server has no VAPID key → web push is off server-side
  | 'denied' // the user blocked notifications in the browser
  | 'off' // supported + permitted, but not subscribed on this device
  | 'on'; // subscribed on this device

export function pushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    typeof Notification !== 'undefined'
  );
}

let swReg: ServiceWorkerRegistration | null = null;

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return null;
  try {
    swReg = await navigator.serviceWorker.register('/sw.js');
    return swReg;
  } catch (e) {
    console.warn('[push] service worker registration failed', e);
    return null;
  }
}

async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (swReg) return swReg;
  if (!pushSupported()) return null;
  swReg = (await navigator.serviceWorker.getRegistration()) || (await registerServiceWorker());
  return swReg;
}

// VAPID application server key: URL-safe base64 → Uint8Array for pushManager.subscribe.
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

/** Current push state for this device — drives the Settings toggle. */
export async function pushState(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const { key } = await api.vapidPublicKey().catch(() => ({ key: '' }));
  if (!key) return 'unconfigured';
  const reg = await getRegistration();
  if (!reg) return 'unsupported';
  const sub = await reg.pushManager.getSubscription();
  return sub ? 'on' : 'off';
}

/**
 * Subscribe this device to push. Must be called from a user gesture (permission prompt).
 * Returns the resulting state so the caller can reflect it / surface why it failed.
 */
export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';

  // Prompt first, directly on the user gesture — some browsers reject requestPermission
  // if an await (e.g. a fetch) runs before it.
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';

  const { key } = await api.vapidPublicKey().catch(() => ({ key: '' }));
  if (!key) return 'unconfigured';

  const reg = await getRegistration();
  if (!reg) return 'unsupported';

  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ||
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key),
    }));

  const json = sub.toJSON();
  await api.pushSubscribe({
    endpoint: sub.endpoint,
    keys: { p256dh: json.keys?.p256dh || '', auth: json.keys?.auth || '' },
  });
  return 'on';
}

/** Unsubscribe this device and tell the backend to drop the row. */
export async function disablePush(): Promise<PushState> {
  const reg = await getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (sub) {
    const endpoint = sub.endpoint;
    await sub.unsubscribe().catch(() => {});
    await api.pushUnsubscribe(endpoint).catch(() => {});
  }
  return 'off';
}
